import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(value, locale = 'pt-BR', currency = 'BRL') {
  if (value === null || value === undefined) return '-';
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(value);
}

// Parse date string without timezone issues
// Handles "YYYY-MM-DD" format correctly for local timezone
export function parseLocalDate(dateString) {
  if (!dateString) return null;
  
  // If it's already a Date object, return it
  if (dateString instanceof Date) return dateString;
  
  // Handle ISO string with time (has T)
  if (typeof dateString === 'string' && dateString.includes('T')) {
    return new Date(dateString);
  }
  
  // Handle "YYYY-MM-DD" format - parse as local date, not UTC
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }
  
  return new Date(dateString);
}

export function formatDate(dateString, options = {}) {
  if (!dateString) return '-';
  
  const date = parseLocalDate(dateString);
  if (isNaN(date.getTime())) return '-';
  
  // Get day of week abbreviation
  const daysOfWeek = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const dayOfWeek = daysOfWeek[date.getDay()];
  
  const defaultOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options
  };
  
  const formattedDate = date.toLocaleDateString('pt-BR', defaultOptions);
  
  // Return with day of week prefix
  return `${dayOfWeek}, ${formattedDate}`;
}

// Format date without day of week (for tables with limited space)
export function formatDateShort(dateString) {
  if (!dateString) return '-';
  
  const date = parseLocalDate(dateString);
  if (isNaN(date.getTime())) return '-';
  
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

// Fuso horário padrão para exibição de horários no app.
// Setado para Manaus (UTC-4) por padrão — alinha com a sede operacional. Mesmo
// que o navegador do usuário esteja com outro TZ (ex: Brasília em UTC-3), os
// timestamps "last_login", "created_at" etc serão exibidos consistentemente.
export const APP_TIMEZONE = 'America/Manaus';
export const APP_TIMEZONE_SHORT = 'Manaus';

export function formatDateTime(dateString) {
  if (!dateString) return '-';
  
  const date = parseLocalDate(dateString);
  if (isNaN(date.getTime())) return '-';
  
  const formatted = date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: APP_TIMEZONE,
  });
  return `${formatted} (${APP_TIMEZONE_SHORT})`;
}

export function getStatusBadgeClass(status) {
  const statusClasses = {
    pending: 'bg-gray-900 text-white dark:bg-gray-700',  // Preto para a vencer
    paid: 'bg-green-500 text-white',  // Verde para pago
    received: 'bg-green-500 text-white',  // Verde para recebido
    partially_paid: 'bg-amber-500 text-white',  // Amarelo para parcialmente pago
    partially_received: 'bg-amber-500 text-white',  // Amarelo para parcialmente recebido
    overdue: 'bg-red-500 text-white',  // Vermelho para vencido
    cancelled: 'bg-gray-400 text-white',
    active: 'bg-green-500 text-white',
    inactive: 'bg-gray-400 text-white'
  };
  
  return statusClasses[status] || 'bg-gray-400 text-white';
}

export function getStatusLabel(status) {
  const labels = {
    pending: 'Pendente',
    paid: 'Pago',
    received: 'Recebido',
    partially_paid: 'Parc. Pago',
    partially_received: 'Parc. Recebido',
    overdue: 'Vencido',
    cancelled: 'Cancelado',
    active: 'Ativo',
    inactive: 'Inativo',
    processing: 'Processando',
    completed: 'Concluído',
    error: 'Erro',
    draft: 'Rascunho',
    approved: 'Aprovado',
    published: 'Publicado'
  };
  
  return labels[status] || status;
}

export function isOverdue(dueDate) {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = parseLocalDate(dueDate);
  due.setHours(0, 0, 0, 0);
  return due < today;
}

export function getDaysUntilDue(dueDate) {
  if (!dueDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = parseLocalDate(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffTime = due - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function truncateText(text, maxLength = 50) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function parseCSVDate(dateStr) {
  // Try various date formats
  const formats = [
    /(\d{2})\/(\d{2})\/(\d{4})/,  // DD/MM/YYYY
    /(\d{4})-(\d{2})-(\d{2})/,    // YYYY-MM-DD
    /(\d{2})-(\d{2})-(\d{4})/     // DD-MM-YYYY
  ];
  
  for (const format of formats) {
    const match = dateStr.match(format);
    if (match) {
      if (format === formats[1]) {
        return new Date(match[1], match[2] - 1, match[3]);
      } else {
        return new Date(match[3], match[2] - 1, match[1]);
      }
    }
  }
  
  return new Date(dateStr);
}

export function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

export function hexToHSL(hex) {
  // Remove the hash if present
  hex = hex.replace(/^#/, '');
  
  // Parse the hex values
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
      default:
        h = 0;
    }
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function applyTenantTheme(branding) {
  if (!branding) return;
  
  const root = document.documentElement;
  
  if (branding.primary_color) {
    root.style.setProperty('--tenant-primary', hexToHSL(branding.primary_color));
    root.style.setProperty('--tenant-primary-hex', branding.primary_color);
  }
  
  if (branding.accent_color) {
    root.style.setProperty('--tenant-accent', hexToHSL(branding.accent_color));
    root.style.setProperty('--tenant-accent-hex', branding.accent_color);
  }
  
  if (branding.neutral_bg) {
    root.style.setProperty('--background', hexToHSL(branding.neutral_bg));
  }
  
  if (branding.neutral_text) {
    root.style.setProperty('--foreground', hexToHSL(branding.neutral_text));
  }
}

export const ROLES = [
  { value: 'admin', label: 'Administrador' },
  { value: 'assistente', label: 'Assistente' },
];

export const PAYMENT_METHODS = [
  { value: 'pix', label: 'PIX' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'cartao_debito', label: 'Cartão de Débito' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'cheque', label: 'Cheque' }
];

export const ACCOUNT_TYPES = [
  { value: 'corrente', label: 'Conta Corrente' },
  { value: 'poupanca', label: 'Poupança' },
  { value: 'investimento', label: 'Investimento' }
];
